import { timeToSlot } from '@/src/hooks/srp/flightTimingFilterManager'
import type { AIPreferences, AIPriority, AISortPreference, RankingWeights } from '@/src/types/aiRecommendation'
import { FilterData, FilterDurationRange, FilterPriceRange, FlightCard, Sectors, SortDataArrays } from '@/src/types/flight'
import type { ActivePriceField } from '@/src/types/srpJourneyIndex'
import { ProcessedFlightsIndex } from '../aiRecommendation/metrics'
import { buildProcessedFlightsIndex } from '../aiRecommendation/ranking'
import { getViaAirportCodes } from '../aiRecommendation/ranking/scoring'
import { cardGrouping } from './cardGrouping'

function isRangeActive (min?: number, max?: number) {
    return !isNaN(Number(min)) && !isNaN(Number(max))
}



function convertHoursToMinutes (hours?: number) {
    return Number(hours) * 60
}

/** Same bucketing as buildMulticityFilterData minutesToHours — filter slider works in whole hours. */
function minutesToFilterHours (minutes: number) {
    return minutes > 0 ? Math.ceil(minutes / 60) : 0
}

/** Duration/price ranges are built with current = default; only apply after user moves the slider. */
function isUserDurationFilterActive (filter?: FilterDurationRange) {
    if (!filter) return false
    const { min: defaultMin, max: defaultMax, current } = filter
    const { min: currentMin, max: currentMax } = current || {}
    if (!isRangeActive(currentMin, currentMax)) return false
    return currentMin !== defaultMin || currentMax !== defaultMax
}

function isUserPriceFilterActive (filter?: FilterPriceRange) {
    if (!filter) return false
    const { min: defaultMin, max: defaultMax, current } = filter
    const { min: currentMin, max: currentMax } = current || {}
    if (isNaN(Number(currentMin)) || isNaN(Number(currentMax))) return false
    return currentMin !== defaultMin || currentMax !== defaultMax
}

function isDurationWithinHourRange (minutes: number, minHours: number, maxHours: number) {
    const hours = minutesToFilterHours(minutes)
    return hours >= minHours && hours <= maxHours
}

const getSectorAirlineCodes = (sectors: Sectors | undefined, sectorKey?: string): string[] => {
    const segments = sectors?.[sectorKey || '']?.flights?.segments ?? []
    return Array.from(new Set(segments.map(segment => segment?.ac).filter((code): code is string => !!code)))
}

const getViaAirportCodesForSector = (sectors: Sectors | undefined, sectorKey?: string): string[] => {
    const segments = sectors?.[sectorKey || '']?.flights?.segments ?? []
    if (segments.length <= 1) return []
    return segments
        .slice(0, -1)
        .map(segment => segment.arrival?.airportCode)
        .filter((code): code is string => !!code)
}

const parseSectorTimeToSlot = (sectors: Sectors | undefined, sectorKey: string | undefined, timeType: 'departure' | 'arrival') => {
    const sector = sectorKey ? sectors?.[sectorKey] : undefined
    const time =
        timeType === 'departure'
            ? sector?.firstDeparture?.time?.replace(':', '').trim()
            : sector?.lastArrival?.time?.replace(':', '').trim()
    return timeToSlot(parseInt(time || '0', 10))
}

function getEffectivePriceForSort (card: FlightCard, shouldShowMultiAirlinePrices: boolean, isPerPaxPricingEnabled: boolean) {
    const {
        priceBreakup: { pr, multiAirlinePrice, totalAdultFare }
    } = card
    if (shouldShowMultiAirlinePrices) return multiAirlinePrice || 0
    if (isPerPaxPricingEnabled) return totalAdultFare || 0
    return pr
}

function sortCardsByPrice (
    cards: FlightCard[],
    direction: 'up' | 'down',
    shouldShowMultiAirlinePrices: boolean,
    isPerPaxPricingEnabled: boolean
) {
    return cards.toSorted((a, b) => {
        const pa = getEffectivePriceForSort(a, shouldShowMultiAirlinePrices, isPerPaxPricingEnabled)
        const pb = getEffectivePriceForSort(b, shouldShowMultiAirlinePrices, isPerPaxPricingEnabled)
        return direction === 'up' ? pa - pb : pb - pa
    })
}

/**
 * Order filtered cards using the API `sortData` list for the selected leg (same source as non-smart SRP sort).
 */
function orderCardsBySortDataIds (
    cards: FlightCard[],
    sortData: SortDataArrays | undefined | null,
    sortKey: keyof SortDataArrays,
    leg: 0 | 1
): FlightCard[] {
    const idList = sortData?.[sortKey]?.[leg]
    if (!idList?.length) return cards.toSorted(() => 0)

    const byId = new Map(cards.map(c => [c.id, c]))
    const used = new Set<string>()
    const ordered: FlightCard[] = []

    for (const id of idList) {
        const card = byId.get(id)
        if (card && !used.has(id)) {
            ordered.push(card)
            used.add(id)
        }
    }
    for (const card of cards) {
        if (!used.has(card.id)) ordered.push(card)
    }
    return ordered
}

function applyAISmartSortOrder ({
    cards,
    sortPreference,
    sortData,
    selectedJourneyLeg,
    shouldShowMultiAirlinePrices,
    isPerPaxPricingEnabled
}: {
    cards: FlightCard[]
    sortPreference: AISortPreference
    sortData: SortDataArrays | undefined | null
    selectedJourneyLeg: 0 | 1
    shouldShowMultiAirlinePrices: boolean
    isPerPaxPricingEnabled: boolean
}): FlightCard[] {
    if (sortPreference === 'price_up') {
        return sortCardsByPrice(cards, 'up', shouldShowMultiAirlinePrices, isPerPaxPricingEnabled)
    }
    if (sortPreference === 'price_down') {
        return sortCardsByPrice(cards, 'down', shouldShowMultiAirlinePrices, isPerPaxPricingEnabled)
    }
    return orderCardsBySortDataIds(cards, sortData, sortPreference, selectedJourneyLeg)
}


export const filterCards = ({
    selectedLegSortedCardIds: cardIds,
    allCardsForSelectedLeg: cards,
    filterData,
    sectors,
    checkedBaggageIncludedRecommendedFilter,
    selectedJourneyLeg,
    shouldShowMultiAirlinePrices,
    isPerPaxPricingEnabled,
    activePriceField,
    smartRecommendationWeights,
    smartRecommendationPreferences,
    smartRecommendationPriority,
    smartRecommendationSortPreference,
    sortData,
    isMulticity = false,
    journeyCount = 1
}: {
    selectedLegSortedCardIds: string[]
    allCardsForSelectedLeg: FlightCard[]
    filterData: FilterData
    sectors?: Sectors
    checkedBaggageIncludedRecommendedFilter: boolean
    selectedJourneyLeg: 0 | 1
    shouldShowMultiAirlinePrices: boolean
    isPerPaxPricingEnabled: boolean
    /** Single per-card price the price-range filter compares (splitLegFare in split view). */
    activePriceField: ActivePriceField
    smartRecommendationWeights: RankingWeights
    smartRecommendationPreferences?: AIPreferences | null
    smartRecommendationPriority?: AIPriority | null
    smartRecommendationSortPreference?: AISortPreference | null
    sortData?: SortDataArrays | null
    isMulticity?: boolean
    journeyCount?: number
}): {
    filteredCards: FlightCard[]
    smartSortIdMetrics?: ProcessedFlightsIndex
    groupedCards: [string, FlightCard[]][]
    smartPreferenceGroupedCards?: {
        preferred: [string, FlightCard[]][]
        nonPreferred: [string, FlightCard[]][]
    }
} => {
    // console.count('filterCards')
    // console.log('smartRecommendationWeights', smartRecommendationWeights)
    // console.log('sortKey', sortKey)


    const isSmartSortSelected = cardIds?.length === 0 // non smart sort id are generated on API level
    // console.log({ isSmartSortSelected })


    //for smart sort
    if (isSmartSortSelected) {
        const fcs = cards.filter(card => applyFiltersToCards({
            card,
            filters: filterData,
            sectors,
            checkedBaggageIncludedRecommendedFilter,
            selectedJourneyLeg,
            shouldShowMultiAirlinePrices,
            isPerPaxPricingEnabled,
            activePriceField,
            isMulticity,
            journeyCount
        }))


        const { smartSortIdMetrics, rankedSortedFlights } = buildProcessedFlightsIndex({
            flights: fcs,
            weights: smartRecommendationWeights,
            context: {
                shouldShowMultiAirlinePrices,
                isPerPaxPricingEnabled,
                preferredDepartureSlot: null,
                departureTimePreference: smartRecommendationPreferences?.departureTime ?? null,
                arrivalTimePreference: smartRecommendationPreferences?.arrivalTime ?? null,
                layoverAirportPreference: smartRecommendationPreferences?.layoverAirport ?? null,
                priority: smartRecommendationPriority ?? null,
                sectors
            }
        })

        const hasExplicitSmartSortPreference =
            !!smartRecommendationSortPreference && smartRecommendationSortPreference !== 'smart'
        const hasTimePreference = Boolean(
            smartRecommendationPreferences?.departureTime || smartRecommendationPreferences?.arrivalTime
        )

        const baseOrderedFlights = hasExplicitSmartSortPreference
            ? applyAISmartSortOrder({
                cards: fcs,
                sortPreference: smartRecommendationSortPreference!,
                sortData,
                selectedJourneyLeg,
                shouldShowMultiAirlinePrices,
                isPerPaxPricingEnabled
            })
            : rankedSortedFlights

        if (hasTimePreference) {
            const preferredFlights = baseOrderedFlights.filter(card => !smartSortIdMetrics[card.id]?.isTimePreferenceMismatch)
            const nonPreferredFlights = baseOrderedFlights.filter(card => smartSortIdMetrics[card.id]?.isTimePreferenceMismatch)
            const groupedPreferred = cardGrouping(preferredFlights, shouldShowMultiAirlinePrices)
            const groupedNonPreferred = cardGrouping(nonPreferredFlights, shouldShowMultiAirlinePrices)
            return {
                filteredCards: [...preferredFlights, ...nonPreferredFlights],
                smartSortIdMetrics,
                groupedCards: [...groupedPreferred, ...groupedNonPreferred],
                smartPreferenceGroupedCards: {
                    preferred: groupedPreferred,
                    nonPreferred: groupedNonPreferred
                }
            }
        }

        const filteredSmart = hasExplicitSmartSortPreference ? baseOrderedFlights : rankedSortedFlights

        return {
            filteredCards: filteredSmart,
            smartSortIdMetrics,
            groupedCards: cardGrouping(filteredSmart, shouldShowMultiAirlinePrices)
        }

    }

    //for other sort

    const filteredCards: FlightCard[] = []
    cardIds.forEach(cardId => {
        const index = Number(cardId.split(':')[1])
        const card = cards[index]
        if (
            applyFiltersToCards({
                card,
                filters: filterData,
                sectors,
                checkedBaggageIncludedRecommendedFilter,
                selectedJourneyLeg,
                shouldShowMultiAirlinePrices,
                isPerPaxPricingEnabled,
                activePriceField,
                isMulticity,
                journeyCount
            })
        ) {
            filteredCards.push(card)
        }
    })







    return {
        filteredCards,
        groupedCards: cardGrouping(filteredCards, shouldShowMultiAirlinePrices)
    }
}

/**
 * Multicity: per-journey filters use filterData[journeyIndex] with AND across journeys.
 * Card-level filters (price, baggage) use filterData[0] / card fields.
 */
function applyMulticityFiltersToCard ({
    card,
    filters,
    sectors,
    checkedBaggageIncludedRecommendedFilter,
    shouldShowMultiAirlinePrices,
    isPerPaxPricingEnabled,
    journeyCount
}: {
    card: FlightCard
    filters: FilterData
    sectors?: Sectors
    checkedBaggageIncludedRecommendedFilter: boolean
    shouldShowMultiAirlinePrices: boolean
    isPerPaxPricingEnabled: boolean
    journeyCount: number
}) {
    const activeJourneyCount = Math.min(journeyCount, card.sectorKeys?.length ?? 0)
    const { NewStops, airlineFilter, tripDuration, layoverDuration, layover_Airport, departureTimeFrom, arrivalTimeFrom, nbAirportFilter } =
        filters

    // Airline filter is global across all journeys — card passes if any journey contains a selected airline
    const { selectedValues: airlineSelectedValues } = airlineFilter?.[0] || {}
    if (airlineSelectedValues?.length) {
        const cardHasSelectedAirline = card.sectorKeys
            .slice(0, activeJourneyCount)
            .some(sk => getSectorAirlineCodes(sectors, sk).some(code => airlineSelectedValues.includes(code)))
        if (!cardHasSelectedAirline) return false
    }

    for (let journeyIndex = 0; journeyIndex < activeJourneyCount; journeyIndex++) {
        const sectorKey = card.sectorKeys[journeyIndex]
        const sector = sectorKey ? sectors?.[sectorKey] : undefined
        if (!sector) return false

        const stopsFilter = NewStops?.[journeyIndex]
        const { selectedValues: stopsSelectedValues } = stopsFilter || {}
        if (stopsSelectedValues?.length) {
            const sectorStops = sector.stops ?? 0
            if (!stopsSelectedValues.includes(sectorStops)) return false
        }

        const tripDurationFilter = tripDuration?.[journeyIndex]
        if (isUserDurationFilterActive(tripDurationFilter)) {
            const { min: minTripDuration, max: maxTripDuration } = tripDurationFilter!.current
            const tripMinutes =
                card.tripDurationMapBySector?.[journeyIndex] ?? sector.totalSectorDuration ?? 0
            if (!isDurationWithinHourRange(tripMinutes, minTripDuration, maxTripDuration)) return false
        }

        const layoverDurationFilter = layoverDuration?.[journeyIndex]
        if (isUserDurationFilterActive(layoverDurationFilter)) {
            const { min: minLayoverDuration, max: maxLayoverDuration } = layoverDurationFilter!.current
            const layoverMinutes = card.layoverDurationMapBySector?.[journeyIndex] ?? 0
            if (!isDurationWithinHourRange(layoverMinutes, minLayoverDuration, maxLayoverDuration)) return false
        }

        const layoverAirportFilter = layover_Airport?.[journeyIndex]
        const { selectedOptions: selectedLayoverAirports } = layoverAirportFilter || {}
        if (selectedLayoverAirports?.length) {
            const layoverAirportValues = getViaAirportCodesForSector(sectors, sectorKey)
            const selectedLayoverAirportSet = new Set(selectedLayoverAirports.map(value => value?.trim().toUpperCase()))
            if (!layoverAirportValues.some(value => selectedLayoverAirportSet.has(value.toUpperCase()))) return false
        }

        const departureFilter = departureTimeFrom?.[journeyIndex]
        const { selectedValues: departureSelectedValues } = departureFilter || {}
        if (departureSelectedValues?.length) {
            const timeSlot = parseSectorTimeToSlot(sectors, sectorKey, 'departure')
            if (!timeSlot || !departureSelectedValues.includes(timeSlot)) return false
        }

        const arrivalFilter = arrivalTimeFrom?.[journeyIndex]
        const { selectedValues: arrivalSelectedValues } = arrivalFilter || {}
        if (arrivalSelectedValues?.length) {
            const timeSlot = parseSectorTimeToSlot(sectors, sectorKey, 'arrival')
            if (!timeSlot || !arrivalSelectedValues.includes(timeSlot)) return false
        }

        const nbGroups = nbAirportFilter?.[journeyIndex] || []
        const nbAirportsUpper = (sector.nbAirports || []).map(code => code?.toUpperCase())
        const hasAnyNbSelection = nbGroups.some(group => (group.selectedValues || []).length > 0)
        if (hasAnyNbSelection) {
            for (const group of nbGroups) {
                const selectedValues = group.selectedValues || []
                if (!selectedValues.length) continue

                const selectedSet = new Set(selectedValues.map(value => value.toUpperCase()))
                const groupAirportCode = group.airportCode?.toUpperCase()
                const groupNearbyOptions = new Set((group.options || []).map(option => option.value?.toUpperCase()))
                const selectedNearbyValues = Array.from(selectedSet).filter(value => value !== groupAirportCode)
                const cardHasNearbyForGroup = nbAirportsUpper.some(code => groupNearbyOptions.has(code))
                const matchesNearbyForGroup = selectedNearbyValues.some(value => nbAirportsUpper.includes(value))
                const matchesOriginalForGroup = selectedSet.has(groupAirportCode || '') && !cardHasNearbyForGroup

                if (!matchesNearbyForGroup && !matchesOriginalForGroup) return false
            }
        }
    }

    if (checkedBaggageIncludedRecommendedFilter && card.indicators?.noBaggage === true) {
        return false
    }

    const priceFilter = filters.journeyPrice?.[0]
    if (isUserPriceFilterActive(priceFilter)) {
        const { min: minPrice, max: maxPrice } = priceFilter!.current
        const {
            priceBreakup: { pr, multiAirlinePrice, totalAdultFare }
        } = card
        const price = shouldShowMultiAirlinePrices
            ? multiAirlinePrice || 0
            : isPerPaxPricingEnabled
                ? totalAdultFare || 0
                : pr
        if (price < minPrice || price > maxPrice) return false
    }

    return true
}

/**
 * Airline card filter logic
 *
 * IMPORTANT: This filter function follows a **reject-first** approach.
 * - Each filter condition should return `false` when a card **does not satisfy** a specific criterion.
 * - The function should return `true` by default, allowing cards to pass unless explicitly rejected.
 *
 * This pattern ensures consistency and improves readability as more filters are added.
 *
 * Example:
 * if (!conditionMet) return false;
 * ...
 * return true;
 */
function applyFiltersToCards ({
    card,
    filters,
    sectors,
    checkedBaggageIncludedRecommendedFilter,
    selectedJourneyLeg,
    shouldShowMultiAirlinePrices,
    isPerPaxPricingEnabled,
    activePriceField,
    isMulticity = false,
    journeyCount = 1
}: {
    card: FlightCard
    filters: FilterData
    sectors?: Sectors
    checkedBaggageIncludedRecommendedFilter: boolean
    selectedJourneyLeg: 0 | 1
    shouldShowMultiAirlinePrices: boolean
    isPerPaxPricingEnabled: boolean
    activePriceField: ActivePriceField
    isMulticity?: boolean
    journeyCount?: number
}) {
    if (isMulticity) {
        return applyMulticityFiltersToCard({
            card,
            filters,
            sectors,
            checkedBaggageIncludedRecommendedFilter,
            shouldShowMultiAirlinePrices,
            isPerPaxPricingEnabled,
            journeyCount
        })
    }

    //stops filter
    const { NewStops } = filters
    const targetStops = (NewStops || [])[selectedJourneyLeg] || {}
    const { selectedValues: stopsSelectedValues } = targetStops
    const { maxStopsInSectors } = card
    if (stopsSelectedValues && stopsSelectedValues.length > 0) {
        if (!stopsSelectedValues.includes(maxStopsInSectors)) return false
    }
    //airline filter
    const { airlineFilter: airlineFilters } = filters
    const targetAirlineFilter = (airlineFilters || [])?.[selectedJourneyLeg] || {}
    const { selectedValues: airlineFilterSelectedValues } = targetAirlineFilter || {}
    const { airlineCodes } = card
    if (airlineFilterSelectedValues && airlineFilterSelectedValues?.length > 0) {
        if (!airlineCodes?.some(code => airlineFilterSelectedValues?.includes(code))) return false
    }

    //trip duration filter
    const { tripDuration } = filters
    const targetTripDuration = (tripDuration || [])?.[selectedJourneyLeg] || {}
    const { current: currentTripDurationRange } = targetTripDuration || {}
    const { min: minTripDuration, max: maxTripDuration } = currentTripDurationRange || {}
    if (isRangeActive(minTripDuration, maxTripDuration)) {
        const tripDurationMinMinutes = convertHoursToMinutes(minTripDuration)
        const tripDurationMaxMinutes = convertHoursToMinutes(maxTripDuration)
        const { totalDurationInMinutes } = card
        if (totalDurationInMinutes < tripDurationMinMinutes || totalDurationInMinutes > tripDurationMaxMinutes) {
            return false
        }
    }

    //layover duration filter
    const { layoverDuration } = filters
    const targetLayoverDuration = (layoverDuration || [])?.[selectedJourneyLeg] || {}
    const { current: currentLayoverDurationRange } = targetLayoverDuration || {}
    const { min: minLayoverDuration, max: maxLayoverDuration } = currentLayoverDurationRange || {}
    if (isRangeActive(minLayoverDuration, maxLayoverDuration)) {
        const layoverDurationMinMinutes = convertHoursToMinutes(minLayoverDuration)
        const layoverDurationMaxMinutes = convertHoursToMinutes(maxLayoverDuration)
        const { totalLayoverDurationInMinutes } = card
        if (
            totalLayoverDurationInMinutes < layoverDurationMinMinutes ||
            totalLayoverDurationInMinutes > layoverDurationMaxMinutes
        ) {
            return false
        }
    }

    //layover airport filter
    const { layover_Airport: layoverAirports } = filters
    const targetLayoverAirports = (layoverAirports || [])?.[selectedJourneyLeg] || {}
    const { selectedOptions: selectedLayoverAirports } = targetLayoverAirports
    if (selectedLayoverAirports && selectedLayoverAirports.length > 0) {
        const layoverAirportValues = getViaAirportCodes(card, sectors)
        const selectedLayoverAirportSet = new Set(selectedLayoverAirports.map(value => value?.trim().toUpperCase()))
        if (!layoverAirportValues.some(value => selectedLayoverAirportSet.has(value))) return false
    }

    //flight departure timings filter
    const { departureTimeFrom } = filters
    const targetDepartureTime = (departureTimeFrom || [])?.[selectedJourneyLeg] || {}

    const { selectedValues: departureTimeSelectedValues } = targetDepartureTime || {}

    if (departureTimeSelectedValues && departureTimeSelectedValues?.length > 0) {
        const sectorKey = card.sectorKeys[0]
        const depTimeFromCard = parseInt(sectors?.[sectorKey]?.firstDeparture.time.replace(':', '').trim() || '0')
        const timeSlot = timeToSlot(depTimeFromCard)
        if (!timeSlot || !departureTimeSelectedValues?.includes(timeSlot)) return false
    }

    //arrival flight timings filter
    const { arrivalTimeFrom } = filters
    const targetArrivalTime = (arrivalTimeFrom || [])?.[selectedJourneyLeg] || {}
    const { selectedValues: arrivalTimeSelectedValues } = targetArrivalTime || {}
    if (arrivalTimeSelectedValues && arrivalTimeSelectedValues?.length > 0) {
        const sectorKey = card.sectorKeys[0]
        const arrTimeFromCard = parseInt(sectors?.[sectorKey]?.lastArrival.time.replace(':', '').trim() || '0')
        const timeSlot = timeToSlot(arrTimeFromCard)
        if (!timeSlot || !arrivalTimeSelectedValues?.includes(timeSlot)) return false
    }
    //checked baggage included filter (recommended filter)
    if (checkedBaggageIncludedRecommendedFilter) {
        const { indicators } = card
        if (indicators?.noBaggage === true) return false
    }

    //hide budget airlines filter (recommended filter)
    // if (hideBudgetAirlinesRecommendedFilter) {
    //     const { airlineCodes } = card
    //     if (airlineCodes.some(code => code.startsWith('VY'))) return false
    // }

    //price range filter
    const { journeyPrice } = filters
    const targetJourneyPrice = (journeyPrice || [])?.[selectedJourneyLeg] || {}
    const { current: currentPriceRange } = targetJourneyPrice || {}
    const { min: minPrice, max: maxPrice } = currentPriceRange || {}
    if (!isNaN(Number(minPrice)) && !isNaN(Number(maxPrice))) {
        // Compare against the SAME single price the view shows on the cards/labels (splitLegFare in
        // split view, combined for RT solution, per-pax/retail for OW) so the slider and the cards
        // never disagree. We never mix multiAirlinePrice and splitLegFare on the price surfaces.
        const price = card.priceBreakup[activePriceField] ?? card.priceBreakup.pr
        if (price < minPrice || price > maxPrice) return false
    }

    //included airport filter (nearby airports)
    const { nbAirportFilter: nbAirportFilters } = filters
    const targetNbAirportGroups = (nbAirportFilters || [])?.[selectedJourneyLeg] || []
    const { nbAirports } = card
    const nbAirportsUpper = (nbAirports || []).map(code => code?.toUpperCase())
    const hasAnyNbSelection = targetNbAirportGroups.some(g => (g.selectedValues || []).length > 0)
    if (hasAnyNbSelection) {
        for (let groupIndex = 0; groupIndex < targetNbAirportGroups.length; groupIndex++) {
            const group = targetNbAirportGroups[groupIndex]
            const selectedValues = group.selectedValues || []
            if (!selectedValues.length) continue

            const selectedSet = new Set(selectedValues.map((v: string) => v.toUpperCase()))
            const groupAirportCode = group.airportCode?.toUpperCase()
            const groupNearbyOptions = new Set((group.options || []).map(option => option.value?.toUpperCase()))
            const selectedNearbyValues = Array.from(selectedSet).filter(value => value !== groupAirportCode)

            const cardHasNearbyForThisGroup = nbAirportsUpper.some(code => groupNearbyOptions.has(code))
            const matchesNearbyForThisGroup = selectedNearbyValues.some(value => nbAirportsUpper.includes(value))
            const matchesOriginalForThisGroup = selectedSet.has(groupAirportCode) && !cardHasNearbyForThisGroup

            if (!matchesNearbyForThisGroup && !matchesOriginalForThisGroup) return false
        }
    }


    // Add more filter conditions here
    return true
}
